import React from 'react';

import { useIntl } from 'react-intl';
import { graphql, useFragment } from 'react-relay';

import { AnyAri } from '@atlassian/ari/any-ari';
import { ResourceType } from '@atlassian/search-client/third-party-graph-entity';

import { ProductKeys } from '../../../../../../../../common/constants/products';
import type { SearchGraphResultProps } from '../../../../../../../../common/types';
import { DescriptionBody } from '../../../../../../../../common/ui/description-body';
import { LastModified } from '../../../../../../../../common/ui/last-modified';
import {
	formatKey,
	getAssigneeOrCreator,
	getStatusAndDate,
} from '../../../../../../../../common/ui/search-graph-jira-dc';
import SearchMetadataLink from '../../../../../../../../common/ui/search-metadata-link';
import { SearchResult } from '../../../../../../../../common/ui/search-result';
import {
	enrichExcerpt,
	processExcerpt,
} from '../../../../../../../../common/utils/search-result-utils';

import type { searchGraphJiraDcWorkItem_AtlassianSearchPage$key } from './__generated__/searchGraphJiraDcWorkItem_AtlassianSearchPage.graphql';
import { messages } from './messages';

export const SearchGraphJiraDcWorkItem = ({
	node,
	position,
	cloudId,
	selectedProduct,
	primaryProduct,
	primarySubProduct,
	appMode,
	product,
	icon,
	onResultClicked,
	onResultContextMenu,
	onCopyLinkButtonClicked,
	onSummarizeInChatButtonClicked,
	onContextMenuCopyButtonClicked,
	onContextMenuButtonClicked,
	subtype,
	thirdPartyConfigs,
	mainContentWidth,
}: SearchGraphResultProps<searchGraphJiraDcWorkItem_AtlassianSearchPage$key>): React.JSX.Element => {
	const data = useFragment(
		graphql`
			fragment searchGraphJiraDcWorkItem_AtlassianSearchPage on SearchResultGraphDocument {
				id
				title
				url
				integrationId
				lastModifiedDate
				excerpt
				entity {
					... on ExternalWorkItem {
						assignee {
							thirdPartyUser {
								id
								name
							}
						}
						createdBy {
							thirdPartyUser {
								id
								name
							}
						}
						dueDate
						status
						subtype
						team
						container @optIn(to: "ExternalDataDepotQueryV2") {
							... on ExternalSpace {
								displayName
								url
							}
						}
					}
				}
			}
		`,
		node,
	);

	const { formatMessage } = useIntl();

	const { lastModifiedDate, url, entity, excerpt, title } = data;
	const { dueDate, status, team } = entity || {};

	const integrationId = data.integrationId;

	const lastModifiedComponent = lastModifiedDate ? (
		<LastModified lastModified={lastModifiedDate} />
	) : null;

	const subtypeMap: Record<string, unknown> = {
		work_item: messages.workItemLabel,
		issue: messages.issueLabel,
		other: messages.workItemLabel,
	};

	const label = formatMessage(
		subtypeMap[subtype?.toLowerCase() ?? 'other'] || messages.workItemLabel,
	);

	// Find the specific datasource for this result based on its integrationId
	const resultDatasource =
		thirdPartyConfigs?.[ProductKeys.JiraDC]?.datasources?.find(
			(ds) => ds.integrationId === integrationId,
		) || null;
	const site =
		resultDatasource?.workspaceName ||
		thirdPartyConfigs?.[ProductKeys.JiraDC]?.workspaceName ||
		null;
	const containerName = entity?.container?.displayName;
	const containerUrl = entity?.container?.url;
	const container =
		containerUrl && containerName ? (
			<SearchMetadataLink label={containerName} href={containerUrl} />
		) : (
			containerName
		);

	const headerMetadataParts = [
		label,
		['site', site],
		['container', container],
		['last-modified', lastModifiedComponent],
		['status', getStatusAndDate(status, dueDate)],
		[
			'person',
			getAssigneeOrCreator(
				formatMessage,
				data.entity?.assignee?.thirdPartyUser?.name,
				data.entity?.createdBy?.thirdPartyUser?.name,
			),
		],
	];

	return (
		<SearchResult
			id={data.id}
			product={product}
			productSelected={!!selectedProduct}
			onResultClicked={onResultClicked}
			onResultContextMenu={onResultContextMenu}
			onCopyLinkButtonClicked={onCopyLinkButtonClicked}
			onSummarizeInChatButtonClicked={onSummarizeInChatButtonClicked}
			onContextMenuCopyButtonClicked={onContextMenuCopyButtonClicked}
			onContextMenuButtonClicked={onContextMenuButtonClicked}
			icon={icon}
			description={<DescriptionBody description={processExcerpt(excerpt ?? undefined, true)} />}
			headerMetadataParts={headerMetadataParts}
			title={enrichExcerpt(title, formatKey(team))}
			url={url}
			isThirdParty
			cloudId={cloudId}
			primaryProduct={primaryProduct}
			primarySubProduct={primarySubProduct}
			position={position}
			partial={data}
			appMode={appMode}
			mainContentWidth={mainContentWidth}
		/>
	);
};

export const isSearchGraphJiraDcWorkItem = (
	node?: any,
): node is searchGraphJiraDcWorkItem_AtlassianSearchPage$key => {
	const { resourceType } = AnyAri.parse(node.id);
	return (
		node?.__typename === 'SearchResultGraphDocument' && resourceType === ResourceType.WORK_ITEM
	);
};
